import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

/**
 * PAX-5547 — Algemene agenda: FullCalendar-integratie.
 *
 * Geregistreerd als Alpine.data('agendaKalender') in app.js.
 * De view rendert <div id="fc-kalender"> als mount-target.
 *
 * config: {
 *   eventsUrl:    string  — JSON-feed endpoint (FullCalendar haalt events op)
 *   verplaatsUrl: string  — template met {id}, bv '/admin/agenda/…/afspraken/{id}/verplaats'
 *   detailUrl:    string  — template met {id}, bv '/admin/agenda/…/afspraken/{id}/json'
 *   storeUrl:     string  — POST voor nieuwe afspraken
 *   // filters (initialiseren vanuit URL-params)
 *   zaal_id, afdeling, mijn, invites
 * }
 */
export function agendaKalender(config) {
    return {
        _cal: null,

        // Kalender-weergave
        huidigView: 'timeGridWeek',

        // Filters — persisteren in URL
        filters: {
            zaal_id:  config.zaal_id  || '',
            afdeling: config.afdeling || '',
            mijn:     config.mijn     || false,
            invites:  config.invites  || false,
        },

        // Detail-flyout (geladen via JSON)
        detail: null,
        detailOpen: false,
        detailLaadt: false,

        // Nieuw/bewerk-afspraak modal (bewerkId null = nieuwe afspraak)
        nieuwOpen: false,
        nieuwStart: '',
        nieuwEind:  '',
        nieuwOpslaan: false,
        bewerkId: null,

        // Drag-to-reschedule bevestiging
        verplaatsOpen: false,
        verplaatsPending: null,

        init() {
            const el = document.getElementById('fc-kalender');
            if (!el) return;

            const self = this;
            const eventsUrl = config.eventsUrl || '/admin/agenda/vergaderingen/events';

            this._cal = new Calendar(el, {
                plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
                locale:  'nl',
                firstDay: 1,
                initialView: 'timeGridWeek',
                headerToolbar: false,
                height: 'auto',
                editable: true,
                selectable: true,
                nowIndicator: true,
                dayMaxEvents: true,
                slotMinTime: '07:00:00',
                slotMaxTime: '21:00:00',

                events(fetchInfo, successCallback, failureCallback) {
                    const qs = new URLSearchParams({
                        start:   fetchInfo.startStr,
                        end:     fetchInfo.endStr,
                        zaal_id: self.filters.zaal_id,
                        afdeling: self.filters.afdeling,
                        mijn:    self.filters.mijn    ? '1' : '',
                        invites: self.filters.invites ? '1' : '',
                    });
                    fetch(eventsUrl + '?' + qs)
                        .then(r => r.json())
                        .then(data => successCallback(data))
                        .catch(failureCallback);
                },

                // Klik op vrij slot: nieuw-afspraak form voorinvullen
                select(info) {
                    self.openNieuw(info.startStr, info.endStr);
                    self._cal.unselect();
                },

                // Klik op afspraak: detail laden via JSON
                eventClick(info) {
                    self.openDetail(info.event.id);
                },

                eventDrop(info) {
                    self.initVerplaats(info);
                },

                eventResize(info) {
                    self.initVerplaats(info);
                },
            });

            this._cal.render();
        },

        // ── Navigatie ─────────────────────────────────────────────────────

        vandaag() { this._cal?.today(); },
        vorige()  { this._cal?.prev(); },
        volgende() { this._cal?.next(); },

        weergave(view) {
            this.huidigView = view;
            this._cal?.changeView(view);
        },

        // ── Filters ───────────────────────────────────────────────────────

        filterToepassen() {
            const url = new URL(window.location.href);
            for (const [k, v] of Object.entries(this.filters)) {
                if (v === '' || v === false || v === null) {
                    url.searchParams.delete(k);
                } else {
                    url.searchParams.set(k, v === true ? '1' : v);
                }
            }
            history.replaceState(null, '', url.toString());
            this._cal?.refetchEvents();
        },

        // ── Detail-flyout ─────────────────────────────────────────────────

        openDetail(eventId) {
            const tpl = config.detailUrl || '/admin/agenda/vergaderingen/afspraken/{id}/json';
            const url = tpl.replace('{id}', eventId);

            this.detail      = null;
            this.detailOpen  = true;
            this.detailLaadt = true;

            fetch(url)
                .then(r => r.json())
                .then(data => {
                    this.detailLaadt = false;
                    if (data.ok) {
                        this.detail = {
                            ...data.afspraak,
                            mijn_antwoord: data.mijn_antwoord,
                            mag_rsvp: data.mag_rsvp,
                            kan_bewerken: data.kan_bewerken,
                        };
                    }
                })
                .catch(() => { this.detailLaadt = false; });
        },

        sluitDetail() {
            this.detailOpen = false;
            this.detail     = null;
        },

        formatDt(isoStr, allDay) {
            if (!isoStr) return '';
            const d = new Date(isoStr);
            if (isNaN(d.getTime())) return isoStr;
            if (allDay) {
                return d.toLocaleDateString('nl-BE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
            }
            return d.toLocaleString('nl-BE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        },

        // ── RSVP ──────────────────────────────────────────────────────────

        async rsvp(afspraakId, antwoord) {
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content || '';
            const body = new URLSearchParams({ _token: csrf, antwoord });

            try {
                const response = await fetch(`/admin/agenda/afspraken/${afspraakId}/rsvp`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Accept': 'application/json',
                    },
                    body: body.toString(),
                });
                const data = await response.json();
                if (!data.ok) {
                    alert(data.fout || 'Je antwoord kon niet worden opgeslagen.');
                    return;
                }

                if (this.detail && Number(this.detail.id) === Number(afspraakId)) {
                    this.detail.mijn_antwoord = data.antwoord;
                }
                this._cal?.refetchEvents();
                window.location.reload();
            } catch {
                alert('Netwerkfout: je antwoord kon niet worden opgeslagen.');
            }
        },

        // ── Nieuw/bewerk-afspraak modal ────────────────────────────────────

        _zetVeld(id, waarde) {
            const el = document.getElementById(id);
            if (!el) return;
            if (el.type === 'checkbox') {
                el.checked = !!waarde;
            } else {
                el.value = waarde ?? '';
            }
        },

        _naarLocal(dt) {
            // "YYYY-MM-DD HH:MM:SS" (DB) of ISO → datetime-local "YYYY-MM-DDTHH:MM"
            return dt ? String(dt).replace(' ', 'T').slice(0, 16) : '';
        },

        openNieuw(start, eind) {
            this.bewerkId   = null;
            this.nieuwStart = start ? start.slice(0, 16) : '';
            this.nieuwEind  = eind  ? eind.slice(0, 16)  : '';

            document.getElementById('fc-afspraak-form')?.reset();
            this._zetVeld('fc-nieuw-start', this.nieuwStart);
            this._zetVeld('fc-nieuw-eind',  this.nieuwEind);
            window.dispatchEvent(new CustomEvent('agenda-prefill', { detail: { genodigden: [] } }));

            this.nieuwOpen = true;
        },

        // Bewerk-modal: zelfde formulier, voorgevuld vanuit de detail-flyout (PAX-8743)
        openBewerk() {
            const d = this.detail;
            if (!d || !d.kan_bewerken) return;

            this.bewerkId = d.id;

            document.getElementById('fc-afspraak-form')?.reset();
            this._zetVeld('fc-nieuw-titel',        d.titel);
            this._zetVeld('fc-nieuw-start',        this._naarLocal(d.start_dt));
            this._zetVeld('fc-nieuw-eind',         this._naarLocal(d.eind_dt));
            this._zetVeld('fc-nieuw-zaal',         d.vergaderzaal_id || '');
            this._zetVeld('fc-nieuw-zicht',        d.zichtbaarheid || 'tenant');
            this._zetVeld('fc-nieuw-omschrijving', d.omschrijving);
            this._zetVeld('fc-nieuw-locatie',      d.locatie_vrij);
            this._zetVeld('fc-nieuw-link',         d.meeting_link);
            this._zetVeld('fc-nieuw-virtueel',     d.virtueel_mogelijk);

            window.dispatchEvent(new CustomEvent('agenda-prefill', {
                detail: { genodigden: (d.genodigden || []).map(g => Number(g.id)) },
            }));

            this.sluitDetail();
            this.nieuwOpen = true;
        },

        sluitNieuw() {
            this.nieuwOpen = false;
            this.bewerkId  = null;
        },

        async submitNieuw($event) {
            $event.preventDefault();
            this.nieuwOpslaan = true;

            const form = $event.target;
            const storeUrl  = config.storeUrl  || '/admin/agenda/vergaderingen/afspraken';
            const updateTpl = config.updateUrl || '/admin/agenda/vergaderingen/afspraken/{id}';
            const url = this.bewerkId ? updateTpl.replace('{id}', this.bewerkId) : storeUrl;

            try {
                const resp = await fetch(url, {
                    method:  'POST',
                    body:    new FormData(form),
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                });
                const data = await resp.json();
                if (data.ok) {
                    this.nieuwOpen = false;
                    this.bewerkId  = null;
                    this._cal?.refetchEvents();
                } else if (data.fout === 'vergaderzaal_bezet') {
                    alert('De vergaderzaal is op dit tijdstip al bezet.');
                } else if (data.fouten) {
                    alert('Controleer de ingevoerde velden: ' + data.fouten.join(', '));
                } else {
                    alert(data.fout || 'Opslaan mislukt.');
                }
            } catch {
                alert('Netwerk fout.');
            } finally {
                this.nieuwOpslaan = false;
            }
        },

        // ── Verwijderen ───────────────────────────────────────────────────

        async verwijderAfspraak(afspraakId) {
            if (!window.confirm('Deze afspraak definitief verwijderen? Genodigden ontvangen een annulering.')) {
                return;
            }

            const tpl  = config.deleteUrl || '/admin/agenda/vergaderingen/afspraken/{id}/verwijderen';
            const url  = tpl.replace('{id}', afspraakId);
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content || '';

            try {
                const resp = await fetch(url, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body:    new URLSearchParams({ _token: csrf }).toString(),
                });
                const data = await resp.json();
                if (data.ok) {
                    this.sluitDetail();
                    this._cal?.refetchEvents();
                } else {
                    alert(data.fout || 'Verwijderen mislukt.');
                }
            } catch {
                alert('Netwerkfout bij verwijderen.');
            }
        },

        // ── Drag-to-reschedule ────────────────────────────────────────────

        initVerplaats(info) {
            this.verplaatsPending = info;
            this.verplaatsOpen    = true;
        },

        bevestigVerplaats() {
            const info = this.verplaatsPending;
            if (!info) return;

            const tpl  = config.verplaatsUrl || '/admin/agenda/vergaderingen/afspraken/{id}/verplaats';
            const url  = tpl.replace('{id}', info.event.id);
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content || '';

            const body = new URLSearchParams({
                _token:       csrf,
                nieuwe_start: info.event.start?.toISOString().slice(0, 19).replace('T', ' ') || '',
                nieuwe_eind:  info.event.end?.toISOString().slice(0, 19).replace('T', ' ')   || '',
            });

            fetch(url, {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    body.toString(),
            })
                .then(r => r.json())
                .then(data => {
                    if (!data.ok) {
                        info.revert();
                        alert(data.fout === 'vergaderzaal_bezet'
                            ? 'De vergaderzaal is op dit tijdstip al bezet.'
                            : (data.fout || 'Verplaatsen mislukt.'));
                    }
                })
                .catch(() => { info.revert(); alert('Netwerk fout bij verplaatsen.'); });

            this.verplaatsOpen    = false;
            this.verplaatsPending = null;
        },

        annuleerVerplaats() {
            this.verplaatsPending?.revert();
            this.verplaatsPending = null;
            this.verplaatsOpen    = false;
        },
    };
}
